// Settings -> Branding: the company's own name, logo, tab icon and colours (shown on the login screen once the
// company code is entered, and in the browser tab / app colours).
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import {
  applyDocumentBranding,
  applyTheme,
  documentTitle,
  loadMyTenantBranding,
  loadPlatformBranding,
  rememberTenantBranding,
  type TenantBranding,
} from "@/lib/branding";
import { TenantBrandingEditor } from "../BrandingEditors";

const CompanyBrandingSettings: React.FC = () => {
  const [brand, setBrand] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyTenantBranding().then((b) => {
      setBrand(b);
      setLoading(false);
    });
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company branding</CardTitle>
        <CardDescription>
          How your company looks when your team signs in: your name, logo, browser tab icon and colours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : !brand ? (
          <p className="text-sm text-muted-foreground">Couldn't load your company's branding.</p>
        ) : (
          <TenantBrandingEditor
            tenantId={brand.id}
            tenantName={brand.tenant_name}
            initial={brand}
            onSaved={async (b) => {
              // Apply straight away so the change is visible without signing out.
              setBrand(b);
              rememberTenantBranding(b);
              applyTheme(b);
              const platform = await loadPlatformBranding();
              applyDocumentBranding({ title: documentTitle(platform, b), faviconUrl: b.favicon_url || platform.favicon_url });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyBrandingSettings;
